import React, { useEffect, useState } from 'react';
import SPAButton from '@/components/admin/SysadminSPA/shared/SPAButton';
import SPACheckbox from '@/components/admin/SysadminSPA/shared/SPACheckbox';
import SPADialog from '@/components/admin/SysadminSPA/shared/SPADialog';

export interface StateOption {
  abbreviation: string;
  checked: boolean;
  id: number;
  name: string;
}

interface StatesDialogContentProps {
  description?: string;
  fetchStates: () => Promise<StateOption[]>;
  onSaved: (selectedLabel: string) => void;
  saveStates: (stateIds: number[]) => Promise<string>;
  title: string;
}

const STATE_GRID = [
  'tw-mt-2 tw-pl-6 tw-grid tw-gap-x-6 tw-gap-y-2',
  'tw-grid-cols-2 sm:tw-grid-cols-3 lg:tw-grid-cols-5',
].join(' ');

// AIDEV-NOTE: The states arrive async, and the loaded list is ~330px of grid. Rendering a one-line
// "Loading…" first meant the dialog opened short and then jumped — and being transform-centred, it
// moved vertically as it grew. The placeholder reuses STATE_GRID at the real row height so the
// dialog opens at its final size.
//
// The count only has to be close: its job is reserving space, not being an inventory. The endpoint
// returns the states plus DC and territories (53 at time of writing).
const PLACEHOLDER_STATE_COUNT = 53;

const StatesSkeleton = () => (
  <>
    <div className="tw-h-5 tw-w-24 tw-bg-spa-bg-skeleton tw-rounded tw-animate-pulse" />
    <div className={STATE_GRID}>
      {Array.from({ length: PLACEHOLDER_STATE_COUNT }, (_, i) => (
        <div key={i} className="tw-h-5 tw-bg-spa-bg-skeleton-subtle tw-rounded tw-animate-pulse" />
      ))}
    </div>
  </>
);

// AIDEV-NOTE: SPA rebuild of the jQuery States.picker checkbox grid, shared by the program license
// and lesson state dialogs. The legacy picker built its own modal *inside* the DataTables cell;
// because SPADialog's panel is transform-centred it became the containing block for that
// position:fixed popup, which trapped and clipped it. Rendering as a normal Radix dialog sidesteps
// that entirely. Consumers own the dialog root (state-driven or routed) and the endpoints;
// `saveStates` resolves to the server's new summary label, handed back through `onSaved`.
const StatesDialogContent = ({
  description,
  fetchStates,
  onSaved,
  saveStates,
  title,
}: StatesDialogContentProps) => {
  const [states, setStates] = useState<StateOption[] | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchStates()
      .then((options) => {
        setStates(options);
        setSelected(new Set(options.filter(state => state.checked).map(state => state.id)));
      })
      .catch(() => setError(true));
  }, []);

  const allSelected = !!states && states.length > 0 && selected.size === states.length;
  const someSelected = selected.size > 0 && !allSelected;

  const toggle = (id: number) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set((states ?? []).map(state => state.id)));
  };

  const handleSubmit = () => {
    setIsSaving(true);
    saveStates([...selected])
      .then(label => onSaved(label))
      .catch(() => setError(true))
      .finally(() => setIsSaving(false));
  };

  return (
    <SPADialog.Content size="lg">
      <SPADialog.Header>
        <SPADialog.Title>{title}</SPADialog.Title>
        <SPADialog.Description className="tw-mt-1 tw-text-spa-secondary">
          {description}
        </SPADialog.Description>
        <SPADialog.CloseButton />
      </SPADialog.Header>

      <SPADialog.Body>
        {error && (
          <p className="tw-m-0 tw-text-sm tw-text-spa-error">
            Couldn’t load states. Close and try again.
          </p>
        )}
        {!states && !error && <StatesSkeleton />}
        {states && !error && (
          <>
            <SPACheckbox
              checked={allSelected}
              indeterminate={someSelected}
              labelClassName="tw-font-medium tw-text-spa-heading"
              onChange={toggleAll}
            >
              Select All
            </SPACheckbox>
            {/* Indented a checkbox-width under Select All, so the states read as its children. */}
            <div className={STATE_GRID}>
              {states.map(state => (
                <SPACheckbox
                  key={state.id}
                  checked={selected.has(state.id)}
                  value={state.id}
                  onChange={() => toggle(state.id)}
                >
                  {state.name}
                </SPACheckbox>
              ))}
            </div>
          </>
        )}
      </SPADialog.Body>

      <SPADialog.Footer>
        <SPADialog.Close asChild>
          <SPAButton>Cancel</SPAButton>
        </SPADialog.Close>
        <SPAButton
          disabled={!states || isSaving}
          variant="primary"
          onClick={handleSubmit}
        >
          {isSaving ? 'Saving…' : 'Save states'}
        </SPAButton>
      </SPADialog.Footer>
    </SPADialog.Content>
  );
};

export default StatesDialogContent;
